/**
 * Shared reservation field normalization.
 * API rows may use many aliases for the same logical fields.
 */

export function readFirst(...values) {
   for (const value of values) {
      if (value === undefined || value === null) continue;
      if (typeof value === "string" && !value.trim()) continue;
      return value;
   }
   return null;
}

const RESERVATION_ROOT_KEYS = ["reservation"];

function reservationSource(row) {
   if (!row || typeof row !== "object") return {};
   const nested = row.reservation;
   if (nested && typeof nested === "object") {
      return { ...nested, ...row };
   }
   return row;
}

export function readReservationId(row) {
   const source = reservationSource(row);
   const id = readFirst(
      source.id,
      source._id,
      source.reservationId,
      source.reservation_id,
      source.uuid,
   );
   return id != null ? String(id) : "";
}

export function readReservationStartAt(row) {
   const source = reservationSource(row);
   const start = readFirst(
      source.startTime,
      source.start,
      source.start_time,
      source.startAt,
      source.startsAt,
      source.dateTime,
      source.datetime,
      source.date,
      source.begin,
   );
   return start != null ? String(start) : "";
}

export function readReservationUserId(row) {
   const source = reservationSource(row);
   return readFirst(
      source.userId,
      source.user_id,
      source.studentId,
      source.student_id,
      source.user?.id,
      source.student?.id,
      source.client?.id,
      source.person?.id,
   );
}

export function readReservationInstructorId(row) {
   const source = reservationSource(row);
   return readFirst(
      source.instructorId,
      source.instructor_id,
      source.instructor?.id,
      source.teacher?.id,
      source.staff?.id,
   );
}

/**
 * Canonical reservation shape for general UI / dedupe keys.
 */
export function normalizeReservation(row, indexFallback = 0) {
   const source = reservationSource(row);
   const id = readReservationId(source);
   const startAt = readReservationStartAt(source);
   const userId = readReservationUserId(source);
   const instructorId = readReservationInstructorId(source);

   return {
      id: id || null,
      userId: userId != null ? String(userId) : "",
      instructorId: instructorId != null ? String(instructorId) : "",
      startAt,
      endAt: readFirst(
         source.endTime,
         source.end,
         source.end_time,
         source.endAt,
         source.endsAt,
      ),
      status: readFirst(source.status, source.state, ""),
      type: readFirst(source.type, source.reservationType, ""),
      sector: readFirst(source.sector, source.location, ""),
      color: readFirst(source.color, source.eventColor, ""),
      raw: source,
      indexFallback,
   };
}

export function reservationRowKey(row, indexFallback = 0) {
   const normalized = normalizeReservation(row, indexFallback);
   if (normalized.id) return `id:${normalized.id}`;

   const fallbackSig = `${normalized.startAt}|${normalized.instructorId}|${normalized.userId}`;
   if (fallbackSig.replace(/\|/g, "").trim()) return `sig:${fallbackSig}`;
   return `idx:${indexFallback}`;
}
